import { formatCount } from '@dbrain/app-core/format';
import { formatPlanTokens, formatTry, type PlanDto, type ProInterestDto } from '@dbrain/shared';
import { Button, Dialog, IconButton, Icons } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useId, useMemo, useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { usePayments } from '../../api/billing.js';
import { api } from '../../api/client.js';
import { useMe } from '../../api/hooks.js';
import { planKeys, usePlan, usePlanCatalog, usePlanUsage } from '../../api/plan.js';
import { shortNames } from '../../pages/bireysel/site-plans.js';

/**
 * Phase 16a: "Pro'ya geç" anywhere (the bar's button, the limits' cards) opens one note while purchase is not open.
 * Phase 16c: where a payment provider is configured it goes to the checkout instead (B2cOdeme). 16c.1: the note is
 * B2cProYakinda (the two packages side by side and "Bana haber ver", once); a Pro model picked on Ücretsiz opens the
 * same dialog titled with the model, as B2cModelPro while payments are open ("Pro'ya geç" to the checkout) and with
 * the "yakında" words and the notice box while they are closed. 16c.2: both in Oppy's own colours and the mockups'
 * layout (the mark, title and words at the top, Ücretsiz narrow beside Pro), every count written the Turkish way.
 */
interface Offer {
  open: () => void;
  compare: () => void;
  /** a Pro model picked on Ücretsiz: the dialog with its name */
  model: (name: string) => void;
}

const OfferContext = createContext<Offer>({ open: () => undefined, compare: () => undefined, model: () => undefined });

type Shown = { kind: 'soon' } | { kind: 'model'; name: string };

export function PlanOfferProvider({ children }: { children: ReactNode }) {
  const [shown, setShown] = useState<Shown | null>(null);
  const navigate = useNavigate();
  const payments = usePayments();
  // "Paketleri karşılaştır": the prices page (B2cFiyatlar) where Pro can be bought, the dialog where it cannot yet
  const value = useMemo<Offer>(
    () => ({
      open: () => (payments.open ? void navigate('/odeme') : setShown({ kind: 'soon' })),
      compare: () => (payments.open ? void navigate('/fiyatlar') : setShown({ kind: 'soon' })),
      model: (name) => setShown({ kind: 'model', name }),
    }),
    [payments.open, navigate],
  );
  return (
    <OfferContext.Provider value={value}>
      {children}
      {shown ? (
        <ProDialog
          model={shown.kind === 'model' ? shown.name : null}
          payments={payments.open}
          onUpgrade={() => {
            setShown(null);
            void navigate('/odeme');
          }}
          onClose={() => setShown(null)}
        />
      ) : null}
    </OfferContext.Provider>
  );
}

/**
 * `canOffer`: whether this person is offered Pro at all. Phase 16b: a Platform admin never is (no "Ücretsiz paket ·
 * Pro'ya geç", no "Pro'ya geç" or "Paketleri karşılaştır", no sentence that sends them to Pro).
 */
export function usePlanOffer() {
  const { open, compare, model } = useContext(OfferContext);
  const me = useMe();
  return { open, compare, model, canOffer: !me.data?.platformOperator };
}

function Check({ off = false }: { off?: boolean }) {
  return off ? <span className="pro-plan__dash" aria-hidden="true">–</span> : <Icons.check size={15} className="pro-plan__check" aria-hidden="true" />;
}

/** One package's column (B2cProYakinda, B2cModelPro): its price and what it has, from the catalogue */
function PlanColumn({ plan, free, current }: { plan: PlanDto; free: PlanDto | null; current: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const pro = plan.id === 'pro';
  const tokens = formatPlanTokens(plan.dailyTokens, locale);
  const times = free && pro && free.dailyTokens > 0 ? plan.dailyTokens / free.dailyTokens : null;
  const freeMonths = pro && plan.yearlyPriceKurus !== null && plan.monthlyPriceKurus > 0 ? Math.round((plan.monthlyPriceKurus * 12 - plan.yearlyPriceKurus) / plan.monthlyPriceKurus) : 0;
  const own = pro ? plan.chatModels.filter((m) => !free?.chatModels.some((f) => f.id === m.id)).map((m) => m.name) : [];
  const skills = plan.maxOwnSkills;
  const connections = plan.maxOwnConnections;
  const strong = (text: string) => (pro ? <strong>{text}</strong> : text);
  // the mockups' bold words in Pro's column ("Sınırsız", "100 MB"); plain in Ücretsiz's
  const bold = pro ? <strong /> : <span />;
  const rows: { key: string; off?: boolean; content: ReactNode }[] = [
    {
      key: 'tokens',
      content: (
        <>
          {t('b2cModels.offer.tokens', { tokens: '§' }).split('§')[0]}
          {strong(tokens)}
          {t('b2cModels.offer.tokens', { tokens: '§' }).split('§')[1]}
          {times && Number.isInteger(times) && times > 1 ? <span className="pro-plan__times"> · {t('b2cModels.offer.tokensTimes', { times: formatCount(times, locale) })}</span> : null}
        </>
      ),
    },
    { key: 'documents', content: strong(t('b2cModels.offer.documents', { count: plan.maxDocuments, value: formatCount(plan.maxDocuments, locale) })) },
    { key: 'file', content: <Trans i18nKey="b2cModels.offer.fileSize" values={{ mb: formatCount(plan.maxFileMb, locale) }} components={{ b: bold }} /> },
    {
      key: 'models',
      off: plan.chatModels.length === 0,
      content: (
        <>
          {strong(t('b2cModels.offer.models', { count: plan.chatModels.length, value: formatCount(plan.chatModels.length, locale) }))}
          {/* 16c.2: Pro's own beside Ücretsiz's ("Büyük, Orta, Hızlı dahil"); a package with none of its own names its models */}
          {pro && own.length > 0 ? `: ${t('b2cModels.offer.modelsIncluded', { names: shortNames(own) })}` : plan.chatModels[0] ? `: ${plan.chatModels.map((m) => m.name).join(', ')}` : ''}
        </>
      ),
    },
    ...(skills === null && connections === null
      ? [{ key: 'own', content: <Trans i18nKey="b2cModels.offer.unlimitedBoth" components={{ b: bold }} /> }]
      : [
          {
            key: 'skills',
            off: skills === 0,
            content: skills === null ? <Trans i18nKey="b2cModels.offer.unlimitedSkills" components={{ b: bold }} /> : t('b2cModels.offer.skills', { count: skills, value: formatCount(skills, locale) }),
          },
          {
            key: 'connections',
            off: connections === 0,
            content:
              connections === null
                ? <Trans i18nKey="b2cModels.offer.unlimitedConnections" components={{ b: bold }} />
                : connections === 0
                  ? t('b2cModels.offer.noConnections')
                  : t('b2cModels.offer.connections', { count: connections, value: formatCount(connections, locale) }),
          },
        ]),
  ];
  return (
    <section className={pro ? 'pro-plan pro-plan--pro' : 'pro-plan'} aria-label={plan.name} data-testid={`pro-plan-${plan.id}`}>
      <header className="pro-plan__head">
        <h3 className="pro-plan__name">{plan.name}</h3>
        {current ? <span className="pro-plan__tag">{t('b2cModels.offer.current')}</span> : null}
        {pro ? (
          <span className="pro-plan__badge">
            <Icons.skills size={11} aria-hidden="true" /> {t('b2cModels.offer.heavy')}
          </span>
        ) : null}
      </header>
      <p className="pro-plan__price">
        <span className="pro-plan__amount">{formatTry(plan.monthlyPriceKurus, locale, true)}</span>
        {pro ? <span className="pro-plan__per"> {t('b2cModels.offer.perMonth')}</span> : null}
      </p>
      <p className="pro-plan__price-note">
        {pro && plan.yearlyPriceKurus !== null ? (
          <>
            {t('b2cModels.offer.vatYearly', { price: formatTry(plan.yearlyPriceKurus, locale, true) })}
            {freeMonths >= 1 ? <strong className="pro-plan__save"> {t('b2cModels.offer.freeMonths', { count: freeMonths })}</strong> : null}
          </>
        ) : (
          t('b2cModels.offer.noCard')
        )}
      </p>
      <ul className="pro-plan__rows">
        {rows.map((row) => (
          <li key={row.key} className={row.off ? 'pro-plan__row pro-plan__row--off' : 'pro-plan__row'}>
            <Check off={row.off} />
            <span>{row.content}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** "Bana haber ver" (B2cProYakinda): recorded once; when payments open, one mail to the person's address */
function NotifyBox() {
  const { t } = useTranslation();
  const me = useMe();
  const usage = usePlanUsage();
  const queryClient = useQueryClient();
  const ask = useMutation({
    mutationFn: () => api<ProInterestDto>('POST', '/api/plans/pro-interest', {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: planKeys.usage }),
  });
  const email = me.data?.user.email ?? '';
  const done = ask.isSuccess || Boolean(usage.data?.proInterest);
  return (
    <div className={done ? 'pro-notify pro-notify--done' : 'pro-notify'} data-testid="pro-notify">
      <span className="pro-notify__icon" aria-hidden="true">
        {done ? <Icons.check size={16} /> : <Icons.mail size={16} />}
      </span>
      <span className="pro-notify__text" role={done ? 'status' : undefined}>
        <strong>{done ? t('b2cModels.offer.notify.done') : t('b2cModels.offer.notify.title')}</strong>
        <span>{done ? t('b2cModels.offer.notify.doneBody', { email }) : t('b2cModels.offer.notify.body', { email })}</span>
      </span>
      {done ? null : (
        <Button className="pro-notify__action" busy={ask.isPending} onClick={() => ask.mutate()} data-testid="pro-notify-ask">
          {t('b2cModels.offer.notify.action')}
        </Button>
      )}
    </div>
  );
}

function ProDialog({ model, payments, onUpgrade, onClose }: { model: string | null; payments: boolean; onUpgrade: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const titleId = useId();
  const plans = usePlanCatalog().data ?? [];
  const mine = usePlan();
  const free = plans.find((plan) => plan.id === 'free') ?? null;
  const pro = plans.find((plan) => plan.id === 'pro') ?? null;
  const freeModel = free?.chatModels[0]?.name ?? '';
  // B2cModelPro: payments open and a model picked; otherwise B2cProYakinda (titled with the model when one was picked)
  const open = payments && model !== null;
  const title = model ? t('b2cModels.offer.model.title', { model }) : t('b2cModels.offer.soon.title');
  const body = open ? t('b2cModels.offer.model.body', { free: freeModel }) : model ? t('b2cModels.offer.soon.modelBody', { free: freeModel }) : t('b2cModels.offer.soon.body');
  return (
    <Dialog title={title} onClose={onClose} closeLabel={t('common.close')} dismissOnBackdrop size="wide" bare labelledBy={titleId} className="pro-dialog" data-testid={open ? 'pro-model' : 'pro-soon'}>
      <div className="pro-dialog__scroll">
        <header className="pro-dialog__head">
          <span className="pro-dialog__mark" aria-hidden="true">
            <Icons.skills size={22} />
          </span>
          <div className="pro-dialog__heading">
            <h2 id={titleId} className="pro-dialog__title">
              {title}
            </h2>
            <p className="pro-dialog__body">{body}</p>
          </div>
          <IconButton label={t('common.close')} icon={<Icons.close />} onClick={onClose} className="pro-dialog__close" data-testid="dialog-close" />
        </header>
        {free && pro ? (
          <div className="pro-dialog__plans" data-testid="plan-compare">
            <PlanColumn plan={free} free={free} current={mine?.id !== 'pro'} />
            <PlanColumn plan={pro} free={free} current={mine?.id === 'pro'} />
          </div>
        ) : null}
        {!payments ? <NotifyBox /> : null}
        <footer className="pro-dialog__foot">
          {open ? (
            <>
              <span className="pro-dialog__foot-note">{t('b2cModels.offer.cancelAnytime')}</span>
              <Button variant="secondary" size="lg" onClick={onClose} data-testid="pro-model-later">
                {t('b2cModels.offer.notNow')}
              </Button>
              <Button size="lg" icon={<Icons.skills size={15} />} onClick={onUpgrade} data-testid="pro-model-upgrade">
                {t('b2cModels.offer.upgrade')}
              </Button>
            </>
          ) : (
            <>
              <span className="pro-dialog__foot-note">{t('b2cModels.offer.keep')}</span>
              <Button variant="secondary" onClick={onClose} data-testid="pro-soon-close">
                {t('b2cModels.offer.ok')}
              </Button>
            </>
          )}
        </footer>
      </div>
    </Dialog>
  );
}
